import { Box, Grid, Typography } from '@mui/material'
import { useCallback, useEffect, useRef } from 'react'
import {
  InfiniteHitsProvided,
  StateResultsProvided,
} from 'react-instantsearch-core'
import {
  Configure,
  connectInfiniteHits,
  connectStateResults,
  InstantSearch,
} from 'react-instantsearch-dom'

import { StoreItemFragment } from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import {
  PointDisplayPurchase,
  StoreItemArea,
  StoreItemAreaSkeleton,
} from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { ArtistThemeProvider } from '@/components/styles'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { DateTime } from '@/core/entities'
import { App } from '@/core/native'
import * as algolia from '@/lib/algolia'

export * from './fragments'

export type State = {
  artistId: string
  userLoggedInId: string
  isLoggedIn: boolean
  point: string
  keyword: string
}

type StoreItemsCellProps = {
  items: StoreItemFragment[]
  artistId: string
  userLoggedInId: string
  hasNextPage: boolean
  onFetchMore: () => Promise<void>
  onSelectItem: () => void
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {}

const client = algolia.client()

export const StoreRequestProvidableItemSearchTemplate: Template<State, Action> =
  ({ state }) => {
    const { routerNames } = useRouter()
    const { analytics } = useAnalytics()
    const itemResultRef = useRef<{ items: StoreItemFragment[]; page: number }>({
      items: [],
      page: 1,
    })
    const publishedAtTimestampRef = useRef<number>(
      DateTime.nowUnixMilliseconds()
    )

    const handleOnCompleted = useCallback(
      (items: StoreItemFragment[], { page }) => {
        itemResultRef.current = { items, page }
        analytics?.track('view_item_list', {
          item_list_id: 'artist_store_list',
          item_list_name: 'Artist Store Items',
          items: items.map(item => ({
            item_id: item.id,
            item_name: item.title,
            item_category: 'store_item',
            item_category2: item.type,
            item_category3: App.getPlatform(),
            item_category4: page,
            item_category5: state.artistId,
            price: item.price,
          })),
        })
      },
      [analytics, state.artistId]
    )

    const handleSelectItem = useCallback(() => {
      analytics?.track('select_item', {
        item_list_id: `artist_store_list_${state.artistId}`,
        item_list_name: 'Artist Store Items',
        items: itemResultRef.current.items.map(item => ({
          item_id: item.id,
          item_name: item.title,
          item_category: 'store_item',
          item_category2: item.type,
          item_category3: App.getPlatform(),
          item_category4: itemResultRef.current.page,
          item_category5: state.artistId,
          price: item.price,
        })),
      })
    }, [analytics, state.artistId])

    return (
      <ArtistThemeProvider artistId={state.artistId}>
        <Head title={routerNames.StoreRequestProvidableItemSearch} />

        <BaseLayout
          header={{
            back: true,
            title: state.keyword,
            right: state.isLoggedIn && !!config.modules.prepaidPoint && (
              <PointDisplayPurchase point={state.point} />
            ),
          }}
        >
          <InstantSearch
            indexName={algolia.indexes.items}
            searchClient={client}
            refresh
          >
            <Configure
              hitsPerPage={10}
              query={state.keyword}
              filters={algolia.filters.itemStoreArtist(
                state.artistId,
                publishedAtTimestampRef.current,
                undefined,
                true
              )}
            />
            <StoreStateResults onCompleted={handleOnCompleted} />
            <StoreItemsInfiniteHits
              artistId={state.artistId}
              userLoggedInId={state.userLoggedInId}
              onSelectItem={handleSelectItem}
            />
          </InstantSearch>
        </BaseLayout>
      </ArtistThemeProvider>
    )
  }

export const StoreStateResults = connectStateResults<
  Partial<
    StateResultsProvided<
      Omit<StoreItemFragment, 'id'> & { objectID: string }
    > & {
      onCompleted: (
        items: StoreItemFragment[],
        options: { page: number }
      ) => void
    }
  >
>(({ searching, searchResults, onCompleted }) => {
  const { t } = useTranslation('storeRequestProvidableItemSearch')

  useEffect(() => {
    if (!searching && searchResults?.hits?.length != undefined) {
      const items = searchResults.hits.map(({ objectID, ...item }) => ({
        id: objectID,
        ...item,
      }))
      onCompleted &&
        onCompleted(items, { page: (searchResults?.page ?? 0) + 1 })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searching, searchResults?.hits?.length, onCompleted])

  if (searching && !searchResults?.hits?.length) {
    return (
      <Box sx={{ px: 5, py: 2 }}>
        <Grid container spacing={3}>
          {Array.from(Array(9).keys()).map(key => (
            <Grid item xs={4} md={3} key={key}>
              <StoreItemAreaSkeleton />
            </Grid>
          ))}
        </Grid>
      </Box>
    )
  }

  if (!searching && !searchResults?.hits?.length) {
    return (
      <Typography
        variant="body1"
        align="center"
        sx={{ py: 6 }}
        color="text.secondary"
      >
        {t('itemEmpty')}
      </Typography>
    )
  }

  return null
})

const StoreItemsInfiniteHits = connectInfiniteHits<
  InfiniteHitsProvided<Omit<StoreItemFragment, 'id'> & { objectID: string }> & {
    artistId: StoreItemsCellProps['artistId']
    userLoggedInId: StoreItemsCellProps['userLoggedInId']
    onSelectItem: StoreItemsCellProps['onSelectItem']
  },
  {}
>(({ hits, hasMore, artistId, userLoggedInId, onSelectItem, refineNext }) => {
  const items = hits.map(({ objectID, ...item }) => ({ id: objectID, ...item }))
  return (
    <StoreItemsCell
      items={items}
      artistId={artistId}
      userLoggedInId={userLoggedInId}
      hasNextPage={hasMore}
      onSelectItem={onSelectItem}
      onFetchMore={async () => refineNext()}
    />
  )
})

const StoreItemsCell: Cell<StoreItemsCellProps> = ({
  hasNextPage,
  artistId,
  userLoggedInId,
  items,
  onFetchMore,
  onSelectItem,
}) => {
  return (
    <CellFrame sx={{ px: 5, py: 2 }}>
      <ScrollFetcher
        loading={false}
        hasNextPage={hasNextPage}
        onFetchMore={onFetchMore}
        component={Grid}
        componentProps={{ spacing: 3, container: true }}
        itemComponent={Grid}
        itemComponentProps={{ item: true, xs: 4, md: 3 }}
        nodes={items?.map((item, index) => (
          <StoreItemArea
            key={index}
            item={item}
            artistId={artistId}
            onClick={onSelectItem}
            showMenberOnlyIcon={true}
          />
        ))}
        emptyMessage={<Box />}
      />
    </CellFrame>
  )
}
